import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import { formatCurrency } from '@/lib/utils';
import { LeadCostLine, ProjectCostLine, User } from '@/types';
import { router, useForm } from '@inertiajs/react';
import { format } from 'date-fns';
import { Plus } from 'lucide-react';
import { FormEventHandler, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import AddCostForm, { CostFormData } from './AddCostForm';
import { CostsTable } from './CostsTable';
import EditCostForm from './EditCostForm';
import { useCostTypeOptions } from './useCostTypes';
import { CostLine, EntityType, EntityWithCosts, areCostsLocked } from './utils';

interface UniversalCostsComponentProps {
    entity: EntityWithCosts & {
        id: number | string;
        project?: { id: number | string } | null;
    };
    entityType: EntityType;
    entityId: number | string;
    showAddButton?: boolean;
    canManage?: boolean;
}

// Helper to normalize cost line to common format
const normalizeCostLine = (cost: LeadCostLine | ProjectCostLine): CostLine => {
    // All cost line types now use the same field names (date_incurred, created_by)
    // Handle created_by: can be a User object (from relationship) or number (ID)
    let createdByUser: User | undefined = undefined;

    // Check for createdBy relationship (ProjectCostLine)
    if ('createdBy' in cost && cost.createdBy && typeof cost.createdBy === 'object' && 'id' in cost.createdBy) {
        createdByUser = cost.createdBy as User;
    }
    // Check for created_by property that might be a User object (LeadCostLine)
    else if ('created_by' in cost && cost.created_by && typeof cost.created_by === 'object' && 'id' in cost.created_by) {
        createdByUser = cost.created_by as User;
    }

    return {
        id: cost.id,
        description: cost.description,
        amount: cost.amount,
        type: cost.type,
        date_incurred: ('date_incurred' in cost ? cost.date_incurred : '') || '',
        created_by: createdByUser,
    };
};

// Helper to get route base path
const getRoutePath = (
    entityType: EntityType,
    entityId: number | string,
    costId?: number | string,
): {
    store: string;
    update: string;
    destroy: string;
} => {
    const basePath = `/${entityType}s/${entityId}/cost-lines`;
    return {
        store: basePath,
        update: costId ? `${basePath}/${costId}` : basePath,
        destroy: costId ? `${basePath}/${costId}` : basePath,
    };
};

const createCostSchema = (costTypes: string[]) => {
    return z.object({
        description: z.string().min(1, 'Description is required'),
        amount: z
            .string()
            .min(1, 'Amount is required')
            .refine((value) => {
                const numeric = Number(value);
                return !Number.isNaN(numeric) && numeric >= 0;
            }, 'Enter a valid amount'),
        type: costTypes.length > 0 ? z.enum(costTypes as [string, ...string[]]) : z.string().min(1, 'Select a valid cost type'),
        date_incurred: z.date(),
    });
};

export default function UniversalCostsComponent({
    entity,
    entityType,
    entityId,
    showAddButton = true,
    canManage = true,
}: UniversalCostsComponentProps) {
    const { t } = useTranslations();
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedCost, setSelectedCost] = useState<CostLine | null>(null);
    const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof CostFormData, string>>>({});
    const [editDateOpen, setEditDateOpen] = useState(false);

    const costTypeOptions = useCostTypeOptions();
    const defaultType = costTypeOptions.length > 0 ? costTypeOptions[0].value : 'other';

    // Add Cost Form
    const {
        data: addData,
        setData: setAddData,
        post,
        processing: addProcessing,
        errors: addErrors,
        reset: resetAdd,
    } = useForm<CostFormData>({
        description: '',
        amount: '',
        type: defaultType,
        date_incurred: new Date(),
    });

    // Edit Cost Form
    const {
        data: editData,
        setData: setEditData,
        processing: editProcessing,
        errors: editErrors,
        reset: resetEdit,
    } = useForm<CostFormData>({
        description: '',
        amount: '',
        type: defaultType,
        date_incurred: new Date(),
    });

    // Delete state
    const [deleteProcessing, setDeleteProcessing] = useState(false);

    const clearValidationError = (field: keyof CostFormData) => {
        setValidationErrors((prev) => {
            if (!prev[field]) {
                return prev;
            }
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    // Add Cost Handlers
    const handleAddSubmit: FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();
        const costTypes = costTypeOptions.map((opt) => opt.value);
        const schema = createCostSchema(costTypes);
        const result = schema.safeParse(addData);

        if (!result.success) {
            const fieldErrors: Partial<Record<keyof CostFormData, string>> = {};
            result.error.issues.forEach((issue) => {
                const field = issue.path[0];
                if (field) {
                    fieldErrors[field as keyof CostFormData] = issue.message;
                }
            });
            setValidationErrors(fieldErrors);
            toast.error(t('toasts.costs_universalcostscomponent.resolve_errors_add', 'Please resolve the highlighted errors.'));
            return;
        }

        setValidationErrors({});
        const routes = getRoutePath(entityType, entityId);

        // All entities now use date_incurred (standardized field name)
        post(routes.store, {
            onSuccess: () => {
                setAddModalOpen(false);
                resetAdd();
                setValidationErrors({});
                toast.success(t('toasts.costs_universalcostscomponent.cost_added', 'Cost added successfully'));
            },
            onError: () => {
                toast.error(t('toasts.costs_universalcostscomponent.add_failed', 'Failed to add cost'));
            },
        });
    };

    // Edit Cost Handlers
    const handleEdit = (cost: CostLine) => {
        setSelectedCost(cost);
        setEditData({
            description: cost.description,
            amount: cost.amount.toString(),
            type: cost.type,
            date_incurred: new Date(cost.date_incurred),
        });
        setValidationErrors({});
        setEditModalOpen(true);
    };

    const handleEditSubmit: FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();
        if (!selectedCost) return;

        const costTypes = costTypeOptions.map((opt) => opt.value);
        const schema = createCostSchema(costTypes);
        const result = schema.safeParse(editData);

        if (!result.success) {
            const fieldErrors: Partial<Record<keyof CostFormData, string>> = {};
            result.error.issues.forEach((issue) => {
                const field = issue.path[0];
                if (field) {
                    fieldErrors[field as keyof CostFormData] = issue.message;
                }
            });
            setValidationErrors(fieldErrors);
            toast.error(t('toasts.costs_universalcostscomponent.resolve_errors_edit', 'Please resolve the highlighted errors.'));
            return;
        }

        setValidationErrors({});
        const routes = getRoutePath(entityType, entityId, selectedCost.id);

        // Handle different entity types with their respective HTTP methods
        // All entities now use date_incurred (standardized field name)
        if (entityType === 'project') {
            // Projects use PUT
            router.put(
                routes.update,
                {
                    description: editData.description,
                    amount: editData.amount,
                    type: editData.type,
                    date_incurred: editData.date_incurred,
                },
                {
                    onSuccess: () => {
                        setEditModalOpen(false);
                        setSelectedCost(null);
                        resetEdit();
                        setValidationErrors({});
                        toast.success(t('toasts.costs_universalcostscomponent.cost_updated', 'Cost updated successfully'));
                    },
                    onError: () => {
                        toast.error(t('toasts.costs_universalcostscomponent.update_failed', 'Failed to update cost'));
                    },
                },
            );
        } else {
            // Leads use PUT
            router.put(
                routes.update,
                {
                    description: editData.description,
                    amount: editData.amount,
                    type: editData.type,
                    date_incurred: editData.date_incurred,
                },
                {
                    onSuccess: () => {
                        setEditModalOpen(false);
                        setSelectedCost(null);
                        resetEdit();
                        setValidationErrors({});
                        toast.success(t('toasts.costs_universalcostscomponent.cost_updated', 'Cost updated successfully'));
                    },
                    onError: () => {
                        toast.error(t('toasts.costs_universalcostscomponent.update_failed', 'Failed to update cost'));
                    },
                },
            );
        }
    };

    const handleEditClose = () => {
        setEditModalOpen(false);
        setSelectedCost(null);
        resetEdit();
        setValidationErrors({});
    };

    // Delete Cost Handlers
    const handleDelete = (cost: CostLine) => {
        setSelectedCost(cost);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (!selectedCost) return;

        setDeleteProcessing(true);
        const routes = getRoutePath(entityType, entityId, selectedCost.id);
        router.delete(routes.destroy, {
            onSuccess: () => {
                setDeleteModalOpen(false);
                setSelectedCost(null);
                setDeleteProcessing(false);
                toast.success(t('toasts.costs_universalcostscomponent.cost_deleted', 'Cost deleted successfully'));
            },
            onError: () => {
                setDeleteProcessing(false);
                toast.error(t('toasts.costs_universalcostscomponent.delete_failed', 'Failed to delete cost'));
            },
        });
    };

    const handleDeleteClose = () => {
        setDeleteModalOpen(false);
        setSelectedCost(null);
    };

    // Normalize cost lines for the table
    const normalizedCosts = (entity.cost_lines || []).map((cost) => normalizeCostLine(cost as LeadCostLine | ProjectCostLine));

    const entityForTable = {
        ...entity,
        cost_lines: normalizedCosts,
    };

    return (
        <div className="space-y-4">
            {showAddButton && canManage && (
                <div className="flex items-center justify-end">
                    {!areCostsLocked(
                        entity as EntityWithCosts & {
                            project?: { id: number | string } | null;
                            status?: string;
                        },
                        entityType,
                    ) && (
                        <ResponsiveModal open={addModalOpen} onOpenChange={setAddModalOpen} variant="sheet">
                            <ResponsiveModalTrigger asChild>
                                <Button onClick={() => setAddModalOpen(true)}>
                                    <Plus className="mr-2 h-4 w-4" />
                                    {t('common.costs.add_cost', 'Add Cost')}
                                </Button>
                            </ResponsiveModalTrigger>
                            <ResponsiveModalContent size="md">
                                <ResponsiveModalHeader>
                                    <ResponsiveModalTitle>{t('common.costs.add_title', 'Add Cost')}</ResponsiveModalTitle>
                                    <ResponsiveModalDescription>
                                        {t(
                                            'common.costs.add_description',
                                            `Add a new cost to this ${entityType}. This will be subtracted from the ${entityType === 'lead' ? 'estimated value' : 'budget'}.`,
                                        )}
                                    </ResponsiveModalDescription>
                                </ResponsiveModalHeader>

                                {/* The form supplies its own action row, so it is the whole body. */}
                                <ResponsiveModalBody>
                                    <AddCostForm
                                        entityType={entityType}
                                        entity={entity}
                                        data={addData}
                                        setData={(field, value) => {
                                            if (value === null || value === undefined) {
                                                if (field === 'date_incurred') {
                                                    setAddData(field, new Date());
                                                } else {
                                                    setAddData(field, '');
                                                }
                                            } else {
                                                setAddData(field, value as string | Date);
                                            }
                                            clearValidationError(field);
                                        }}
                                        errors={{ ...addErrors, ...validationErrors }}
                                        clearValidationError={(field) => clearValidationError(field)}
                                        onSubmit={handleAddSubmit}
                                        processing={addProcessing}
                                    />
                                </ResponsiveModalBody>
                            </ResponsiveModalContent>
                        </ResponsiveModal>
                    )}
                </div>
            )}

            <CostsTable entity={entityForTable} entityType={entityType} onEdit={handleEdit} onDelete={handleDelete} canManage={canManage} t={t} />

            {/* Edit Modal */}
            {selectedCost && (
                <ResponsiveModal open={editModalOpen} onOpenChange={handleEditClose} variant="sheet">
                    <ResponsiveModalContent size="md">
                        <ResponsiveModalHeader>
                            <ResponsiveModalTitle>{t('common.costs.edit_title', 'Edit Cost')}</ResponsiveModalTitle>
                            <ResponsiveModalDescription>
                                {t('common.costs.edit_description', `Edit this cost line for the ${entityType}.`)}
                            </ResponsiveModalDescription>
                        </ResponsiveModalHeader>

                        {/* The form supplies its own action row, so it is the whole body. */}
                        <ResponsiveModalBody>
                            <EditCostForm
                                entityType={entityType}
                                entity={entity}
                                cost={selectedCost}
                                data={editData}
                                setData={(field, value) => {
                                    if (value === null || value === undefined) {
                                        if (field === 'date_incurred') {
                                            setEditData(field, new Date());
                                        } else {
                                            setEditData(field, '');
                                        }
                                    } else {
                                        setEditData(field, value as string | Date);
                                    }
                                    clearValidationError(field);
                                }}
                                errors={{ ...editErrors, ...validationErrors }}
                                clearValidationError={(field) => clearValidationError(field)}
                                onSubmit={handleEditSubmit}
                                processing={editProcessing}
                                onCancel={handleEditClose}
                                dateOpen={editDateOpen}
                                setDateOpen={setEditDateOpen}
                            />
                        </ResponsiveModalBody>
                    </ResponsiveModalContent>
                </ResponsiveModal>
            )}

            {/* Delete Modal */}
            {selectedCost && (
                <AlertDialog open={deleteModalOpen} onOpenChange={handleDeleteClose}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t('common.costs.delete_title', 'Are you sure?')}</AlertDialogTitle>
                            <AlertDialogDescription>
                                {t(
                                    'common.costs.delete_description',
                                    `This action cannot be undone. This will permanently delete the cost line "${selectedCost.description}".`,
                                )}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="bg-muted/50 space-y-2 rounded-lg p-4">
                            <div className="font-medium">{selectedCost.description}</div>
                            <div className="text-muted-foreground text-sm">
                                Amount: <span className="font-medium">{formatCurrency(Number(selectedCost.amount))}</span>
                            </div>
                            <div className="text-muted-foreground text-sm">
                                Date: <span className="font-medium">{format(new Date(selectedCost.date_incurred), 'yyyy.MM.dd')}</span>
                            </div>
                            {selectedCost.created_by && typeof selectedCost.created_by === 'object' && (
                                <div className="text-muted-foreground text-sm">
                                    Added by:{' '}
                                    <span className="font-medium">
                                        {selectedCost.created_by.first_name} {selectedCost.created_by.last_name}
                                    </span>
                                </div>
                            )}
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel onClick={handleDeleteClose} disabled={deleteProcessing}>
                                {t('common.cancel', 'Cancel')}
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDeleteConfirm}
                                disabled={deleteProcessing}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                {deleteProcessing ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            )}
        </div>
    );
}
